import { cn, cva } from 'noddi-ui-common';
import { formatCompactCount, formatDecimal } from 'noddi-util';
import { Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from 'recharts';
import { webButtonStyling } from '../../../helpers/webButtonStyling';
import { NoddiCard } from '../../Cards/NoddiCard';
import { NoddiSkeleton } from '../../Feedback/NoddiSkeleton';
import { NoddiBadge } from '../../Other/NoddiBadge';
import { useGetPieDimensions } from './useGetPieDimension';

export interface StatusData<T extends string | number = string | number> {
  name: string;
  value: T;
  count: number;
  color: string;
}

interface Props<T extends string | number = string | number> {
  title?: string;
  description?: string;
  data: StatusData<T>[];
  onBadgeClick?: (value: T) => void;
  selectedValues?: T[];
  onSelectionChange?: (values: T[]) => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  className?: string;
  size?: 'default' | 'small' | 'xs';
  tooltip?: string | React.ReactNode;
  isLoading?: boolean;
  centerCount?: number;
  /** When false, only the chart content is rendered (no NoddiCard). Use when wrapping in your own card. Default true. */
  wrapInCard?: boolean;
  /** When true, the badge legend below the chart is hidden. Useful when rendering a custom legend elsewhere. */
  hideLegend?: boolean;
}

const DashboardPieChartContent = <T extends string | number = string | number>({
  data,
  onBadgeClick,
  selectedValues,
  onSelectionChange,
  emptyStateTitle = 'No data available',
  emptyStateDescription,
  className,
  size = 'small',
  isLoading = false,
  centerCount,
  hideLegend = false
}: Pick<
  Props<T>,
  | 'data'
  | 'onBadgeClick'
  | 'selectedValues'
  | 'onSelectionChange'
  | 'emptyStateTitle'
  | 'emptyStateDescription'
  | 'className'
  | 'size'
  | 'isLoading'
  | 'centerCount'
  | 'hideLegend'
>) => {
  const { chartHeight, innerRadius, outerRadius } = useGetPieDimensions(size);
  const totalCount = centerCount ?? data.reduce((acc, item) => acc + item.count, 0);
  const centerLabel = formatCompactCount(totalCount);
  const activeValues = selectedValues ?? [];
  const isSelectable = Boolean(onBadgeClick || onSelectionChange);
  const chartData = data.map((item) => ({ ...item, fill: item.color }));

  const handleItemClick = (value: T) => {
    if (!onSelectionChange) {
      onBadgeClick?.(value);
      return;
    }

    const isSelected = activeValues.includes(value);
    const nextValues = isSelected ? activeValues.filter((item) => item !== value) : [...activeValues, value];

    onSelectionChange(nextValues);
  };

  if (data.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-12 text-center'>
        <div className='mb-2 text-muted-foreground'>{emptyStateTitle}</div>
        <div className='text-sm text-muted-foreground'>{emptyStateDescription}</div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col items-center justify-center', className)}>
      <div className='relative flex w-full items-center justify-center'>
        {isLoading ? (
          <div className='flex items-center justify-center' style={{ height: chartHeight }}>
            <NoddiSkeleton className='rounded-full' style={{ width: outerRadius * 2, height: outerRadius * 2 }} />
          </div>
        ) : (
          <ResponsiveContainer width='100%' height={chartHeight}>
            <PieChart>
              <Pie
                data={chartData}
                cx='50%'
                cy='50%'
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                paddingAngle={5}
                dataKey='count'
                onClick={isSelectable ? (entry) => handleItemClick((entry.payload as StatusData<T>).value) : undefined}
                shape={(props) => {
                  const payload = props.payload as StatusData<T> & { fill?: string };
                  const isSelected = activeValues.includes(payload.value);
                  const fill = payload.fill ?? props.fill ?? payload.color;
                  return (
                    <Sector
                      {...props}
                      fill={fill}
                      stroke={isSelected ? 'currentColor' : undefined}
                      strokeWidth={isSelected ? 2 : undefined}
                      className={cn(isSelectable && 'cursor-pointer')}
                    />
                  );
                }}
              />
              <Tooltip
                cursor={false}
                content={<DashboardPieTooltip />}
                wrapperStyle={{ outline: 'none', zIndex: 20 }}
                contentStyle={{ background: 'transparent', border: 'none', padding: 0 }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
        {!isLoading && (
          // The exact count stays one hover away once the label is compacted.
          <div className='absolute z-0 text-center' title={formatDecimal(totalCount, 0)}>
            <div className={pieCenterCountVariants({ size, length: getLabelLength(centerLabel) })}>{centerLabel}</div>
          </div>
        )}
      </div>
      {!hideLegend && (
        <div className='mt-2 flex flex-wrap justify-center gap-2'>
          {data.map((item, index) => {
            const isActive = activeValues.includes(item.value);
            const content = (
              <>
                <div className='size-3 shrink-0 rounded-full' style={{ backgroundColor: item.color }} />
                <span className='min-w-0 truncate text-sm'>{item.name}:</span>
                {/* min-w here to prevent layout shift when loading */}
                <span className='min-w-4 shrink-0 text-sm'>
                  {isLoading ? <NoddiSkeleton className='size-4' /> : item.count}
                </span>
              </>
            );

            return isSelectable ? (
              <NoddiBadge
                asChild
                variant='outline'
                key={index}
                className={cn(
                  'flex max-w-full cursor-pointer items-center gap-2 ring-1 hover:bg-muted',
                  isActive ? 'ring-primary' : 'ring-transparent'
                )}
              >
                <button
                  type='button'
                  onClick={() => handleItemClick(item.value)}
                  aria-pressed={isActive}
                  className={webButtonStyling}
                >
                  {content}
                </button>
              </NoddiBadge>
            ) : (
              <NoddiBadge
                variant='outline'
                key={index}
                className={cn(
                  'flex max-w-full items-center gap-2 ring-1',
                  isActive ? 'ring-primary' : 'ring-transparent'
                )}
              >
                {content}
              </NoddiBadge>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const DashboardPieChart = <T extends string | number = string | number>({
  title,
  description,
  data,
  onBadgeClick,
  selectedValues,
  onSelectionChange,
  emptyStateTitle = 'No data available',
  emptyStateDescription,
  className,
  size = 'small',
  tooltip,
  isLoading = false,
  centerCount,
  wrapInCard = true,
  hideLegend = false
}: Props<T>) => {
  const content = (
    <DashboardPieChartContent
      data={data}
      onBadgeClick={onBadgeClick}
      selectedValues={selectedValues}
      onSelectionChange={onSelectionChange}
      emptyStateTitle={emptyStateTitle}
      emptyStateDescription={emptyStateDescription}
      className={className}
      size={size}
      isLoading={isLoading}
      centerCount={centerCount}
      hideLegend={hideLegend}
    />
  );

  if (!wrapInCard) {
    return content;
  }

  return (
    <NoddiCard
      title={title}
      description={description}
      descriptionSkeleton={isLoading}
      size={getCardSize(size)}
      tooltip={tooltip}
    >
      {content}
    </NoddiCard>
  );
};

const getCardSize = (size: string) => {
  if (size === 'xs' || size === 'small') {
    return 'small';
  }
  return 'medium';
};

// The donut hole is a fixed diameter per size, so the font steps down as the label gets longer:
// short = up to 3 characters ("999", "10K"), medium = 4-6 ("1 234", "28,8K", "123,5K"),
// long = 7+ ("1,2 mill."). Each step is the largest size whose label still clears the hole on mobile.
const pieCenterCountVariants = cva('font-semibold tabular-nums', {
  variants: {
    size: { xs: '', small: '', default: '' },
    length: { short: '', medium: '', long: '' }
  },
  compoundVariants: [
    { size: 'xs', length: 'short', className: 'text-lg' },
    { size: 'xs', length: ['medium', 'long'], className: 'text-xs' },
    { size: 'small', length: 'short', className: 'text-2xl' },
    { size: 'small', length: 'medium', className: 'text-lg' },
    { size: 'small', length: 'long', className: 'text-xs' },
    { size: 'default', length: 'short', className: 'text-4xl' },
    { size: 'default', length: 'medium', className: 'text-3xl' },
    { size: 'default', length: 'long', className: 'text-xl' }
  ]
});

const getLabelLength = (label: string) => {
  if (label.length <= 3) {
    return 'short';
  }
  return label.length <= 6 ? 'medium' : 'long';
};

const DashboardPieTooltip = ({
  active,
  payload
}: {
  active?: boolean;
  payload?: Array<{ payload: StatusData<string | number> }>;
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const data = payload[0]?.payload;
  if (!data) {
    return null;
  }

  return (
    <div className='z-50 flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-900 shadow-xs'>
      <span className='size-2 shrink-0 rounded-full' style={{ backgroundColor: data.color }} />
      <span className='font-medium'>{data.name}</span>
      <span className='text-gray-600'>•</span>
      <span>{data.count}</span>
    </div>
  );
};
